import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Logo } from "../assets/icons/Icons";
import { getProfileByHospitalAndProfileId, Profile } from "../lib/apis/profile";
import { useUserData } from "../lib/contexts/UserContext";
import { toast } from "react-toastify";

const ProfilePreview: React.FC = () => {
  const { hospital_id, profileId } = useParams();
  const navigate = useNavigate();
  const { accessToken } = useUserData();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(false);

  const handleBack = () => {
    if (hospital_id) {
      navigate(`/${hospital_id}/profiles`);
    }
  };

  const handleContinue = () => {
    if (hospital_id) {
      navigate(`/${hospital_id}`);
    }
  };

  useEffect(() => {
    const fetchProfile = async () => {
      if (!hospital_id || !profileId || !accessToken) return;

      setLoading(true);
      try {
        const response = await getProfileByHospitalAndProfileId(hospital_id, profileId);
        
        if (response?.status === 200 && (response?.data?.status === 1 || response?.data?.status === "SUCCESS")) {
          const profileData = response.data.data || response.data.result || response.data;
          
          if (profileData) {
            setProfile(profileData);
          } else {
            toast.error("Failed to load profile details");
          }
        } else {
          toast.error("Failed to load profile details");
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
        toast.error("Error loading profile details");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [hospital_id, profileId, accessToken]);

  const displayName = profile?.tv_profile_name || profile?.profile_name || profileId?.replace(/-/g, " ").toUpperCase() || "Profile";
  
  const displayView = profile?.view_type 
    ? profile.view_type.charAt(0).toUpperCase() + profile.view_type.slice(1).replace(/-/g, " ")
    : "N/A";
  
  const languageMap: { [key: string]: string } = {
    'en': 'English',
    'kn': 'Kannada',
    'ta': 'Tamil',
    'hi': 'Hindi',
    'ar': 'Arabic',
    'te': 'Telugu',
    'mr': 'Marathi'
  };
  const displayLanguage = profile?.language 
    ? (languageMap[profile.language] || profile.language.toUpperCase()) 
    : "N/A";
  
  const displayDoctors = (() => {
    if (profile?.doctors_list && Array.isArray(profile.doctors_list) && profile.doctors_list.length > 0) {
      const doctorNames = profile.doctors_list.map((doctor: any) => {
        if (typeof doctor === 'object' && doctor !== null) {
          return doctor.name || doctor.full_name || doctor.id || "Unknown Doctor";
        }
        return typeof doctor === 'string' ? doctor : "Unknown Doctor";
      }).filter(name => name && name !== "Unknown Doctor");
      return doctorNames.length > 0 ? doctorNames.join(", ") : "No doctors assigned";
    } else if (profile?.doctor_name) {
      return profile.doctor_name;
    } else if (profile?.doctor_id) {
      return profile.doctor_id;
    }
    return "No doctors assigned";
  })();
  
  const displaySettings = profile ? [
    { label: "Token", value: profile.token ? "Enabled" : "Disabled" },
    { label: "Patient Name", value: profile.patient_name ? "Enabled" : "Disabled" },
    { label: "Room Number", value: profile.room_number ? "Enabled" : "Disabled" },
    { label: "Slot", value: profile.slot ? "Enabled" : "Disabled" },
    { label: "Booking Mode", value: profile.booking_mode ? "Enabled" : "Disabled" },
  ] : null;

  return (
    <div className="flex flex-col items-center justify-center text-center w-full min-h-screen bg-[#F1F5F9] px-4">
      <Logo />
      <div className="bg-white rounded-2xl shadow-md p-8 mt-10 max-w-lg w-full space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0B74FF]"></div>
          </div>
        ) : (
          <>
            <p className="text-2xl font-semibold text-dark">
              {displayName}
            </p>
            {profile ? (
              <div className="space-y-3 text-left">
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-sm font-medium text-gray-600">View Type:</span>
                  <span className="text-sm text-gray-800">{displayView}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-sm font-medium text-gray-600">Language:</span>
                  <span className="text-sm text-gray-800">{displayLanguage}</span>
                </div>
                <div className="flex justify-between items-start py-2 border-b border-gray-200">
                  <span className="text-sm font-medium text-gray-600">Doctors:</span>
                  <span className="text-sm text-gray-800 text-right max-w-[60%]">{displayDoctors}</span>
                </div>
                {displaySettings && displaySettings.length > 0 && (
                  <>
                    <div className="pt-2 mt-2 border-t border-gray-300">
                      <p className="text-sm font-semibold text-gray-700 mb-2">TV Display Settings:</p>
                      {displaySettings.map((setting, index) => (
                        <div key={index} className="flex justify-between items-center py-2 border-b border-gray-200">
                          <span className="text-sm font-medium text-gray-600">{setting.label}:</span>
                          <span className="text-sm text-gray-800">{setting.value}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500">
                Detailed profile view will be available once the integration is complete. For now you can review the saved setup later and continue to the live screen.
              </p>
            )}
          </>
        )}
        <div className="flex flex-col md:flex-row gap-4 mt-6">
          <button
            className="w-full border border-slate-300 rounded-lg py-3 font-semibold text-gray-600 hover:border-sbTextHover"
            onClick={handleBack}
          >
            Back to profiles
          </button>
          <button
            className="w-full bg-sbTextHover text-white rounded-lg py-3 font-semibold"
            onClick={handleContinue}
          >
            Continue to main screen
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfilePreview;

